import { Card, Divider, Flex, Form, Spin } from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'

import {
  useTreatmentServiceGetTreatmentPlanAdverseEffectEntryListEndpoint,
  useTreatmentServiceGetTreatmentPlanEndpoint
} from 'src/api/queries'
import { GetConsultationResponse, Permissions } from 'src/api/requests'
import { Text } from 'src/components'
import { useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { PersonReactionsList } from '../../PersonTreatmentsSection'
import { Recipes } from '../Treatments'

import { useStyles } from './styles'
import { TreatmentConsumablesCard } from './TreatmentConsumablesCard'

type Props = {
  data?: GetConsultationResponse
}

export const TreatmentDetails = ({ data }: Props) => {
  const { t, currentLang } = useLocales()
  const { hasPermission } = useUser()

  const [form] = Form.useForm()
  const { styles } = useStyles()
  const { data: treatmentDetails, isLoading } =
    useTreatmentServiceGetTreatmentPlanEndpoint(
      {
        id: String(data?.treatmentPlanId)
      },
      [String(data?.treatmentPlanId)],
      { enabled: !!data?.treatmentPlanId }
    )

  const { data: adverseReactions } =
    useTreatmentServiceGetTreatmentPlanAdverseEffectEntryListEndpoint(
      {
        id: String(data?.treatmentPlanId)
      },
      [String(data?.treatmentPlanId)],
      {
        enabled: !!data?.treatmentPlanId
      }
    )

  useEffect(() => {
    if (adverseReactions) {
      form?.setFieldsValue(adverseReactions?.data)
    }
  }, [adverseReactions, form])

  if (isLoading) {
    return <Spin />
  }

  return (
    <Card>
      <Card
        title={t('consultations.treatment')}
        className={styles.treatmentCard}
      >
        <Flex gap={36} className={styles.treatmentContent}>
          <Flex vertical gap={8}>
            <Text weight={700}>{t('treatments.treatment_type')}</Text>

            <Flex gap={8} wrap="wrap">
              {treatmentDetails?.treatmentType
                ? treatmentDetails.treatmentType?.translation?.[
                    currentLang?.value
                  ]
                : '-'}
            </Flex>
          </Flex>

          <Flex vertical gap={8}>
            <Text weight={700}>{t('investigations.treatment_reason')}</Text>

            <Flex gap={8} wrap="wrap">
              {treatmentDetails?.preventionTreatmentContingentType
                ? treatmentDetails.preventionTreatmentContingentType
                    ?.translation?.[currentLang?.value]
                : '-'}
            </Flex>
          </Flex>

          <Flex vertical gap={8}>
            <Text weight={700}>{t('treatments.regiment_type')}</Text>

            <Flex gap={8} wrap="wrap">
              {treatmentDetails?.treatmentTypeRegimen
                ? treatmentDetails?.treatmentTypeRegimen?.translation?.[
                    currentLang?.value
                  ]
                : '-'}
            </Flex>
          </Flex>

          <Flex vertical gap={8}>
            <Text weight={700}>{t('persons.treatment.start_date')}</Text>

            <Flex gap={8} wrap="wrap">
              {dayjs(treatmentDetails?.created).format(dayMonthYearBackSlash)}
            </Flex>
          </Flex>

          <Flex vertical gap={8}>
            <Text weight={700}>{t('persons.treatment.end_date')}</Text>

            <Flex gap={8} wrap="wrap">
              {dayjs(treatmentDetails?.endDate).format(dayMonthYearBackSlash)}
            </Flex>
          </Flex>
        </Flex>

        <Divider />

        {hasPermission(
          Permissions.TREATMENT_TREATMENT_PLAN_VIEW_PRESCRIBED
        ) && (
          <Flex key={1} vertical gap={24} className={styles.treatmentContent}>
            <Text size="xl">{t('persons.treatment.treatment_prescribed')}</Text>

            <TreatmentConsumablesCard
              prescribedTreatments={treatmentDetails?.prescribedTreatments}
            />
          </Flex>
        )}

        {hasPermission(
          Permissions.TREATMENT_TREATMENT_PLAN_RECIPE_VIEW_ALL
        ) && <Recipes treatmentPlan={treatmentDetails?.id} hideCreateButton />}

        {!!adverseReactions?.data?.length && (
          <PersonReactionsList
            data={adverseReactions?.data}
            treatmentPlanId={String(data?.treatmentPlanId)}
          />
        )}
      </Card>
    </Card>
  )
}
